import { FunctionComponent as F, useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useAppDispatch } from "../../core/store/hooks";
import { IState } from "../../core/store/store";
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListingsTable from "./ListingsTable";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IconButton from "@mui/material/IconButton";
import loadingGif from '../../images/loading.gif';
import { updateProductId } from "../../core/store/listings/listings.slice";
import { retrieveListingsAsync } from "../../core/store/listings/listings.thunks";
import { getProductAsync } from "../../core/store/products/products.thunks";
import DeleteIcon from '@mui/icons-material/Delete';
import { addToProtocolAsync, removeListingAsync } from "../../core/store/protocol/protocol.thunks";
import Link from '@mui/material/Link';
import { PROTOCOL } from "../../core/router/paths";

interface LocationState
{
  swapDosingId?: number,
  retrieveListings?: boolean,
  productId?: number,
  backButton?: boolean
}

interface Params
{
  productId?: string,
  protocolId?: string
}

const ChooseListing: F = () =>
{
  const navigate = useNavigate();
  const location = useLocation();
  const [locationState] = useState<LocationState | undefined>(location.state);
  const params = useParams() as Params;
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();

  const storedProductId = useSelector((state: IState) => state.listings.productId);

  const allListings = (useSelector((state: IState) => state.listings.listings) || []);
  const individualListings = allListings.filter(l => !l.bundleId);
  const bundleListings = allListings.filter(l => l.bundleId);

  const [listingsRetrieved, setListingsRetrieved] = useState(false);

  const products = useSelector((state: IState) => state.products.products);

  const productId =
    locationState?.productId ||
    (params.productId ? Number(params.productId) : undefined) ||
    storedProductId;
  let product = products?.find(p => p.productId === productId);

  const dosing = useSelector((state: IState) => state.protocol.protocols
      .find(p => p.protocolId === Number(params?.protocolId))?.dosings
      ?.find(d => d.dosingId === locationState?.swapDosingId));

  useEffect(() =>
  {
    if(!productId)
      return;

    if(locationState?.retrieveListings === false)
    {
      setListingsRetrieved(true);
      return;
    }

    dispatch(updateProductId(productId));

    if(!product)
      appDispatch(getProductAsync(productId)).then(p => product = p);

    appDispatch(retrieveListingsAsync(productId, Number(params.protocolId)))
      .then(() => setListingsRetrieved(true));
  }, []);


  if(!productId)
    navigate("../", { relative: 'path', state: locationState });

  function handleRemoveVendorFromProtocolClick()
  {
    if(!locationState?.swapDosingId)
      return;

    appDispatch(removeListingAsync(Number(params.protocolId), locationState?.swapDosingId));
    navigate(-1);
  }

  function handleSkipClick()
  {
    appDispatch(addToProtocolAsync(Number(params.protocolId), undefined, productId));
    navigate(PROTOCOL + "/" + params.protocolId);
  }

  return (
    <>
      <Dialog
        maxWidth='md'
        fullWidth
        PaperProps={{ sx: { p: '15px' }}}
        open={true}
        onClose={() => navigate("../../../", { relative: 'path', state: locationState })}>
        {(locationState?.backButton !== false) ?
          <IconButton
            disableRipple
            onClick={() => navigate("../../", { relative: 'path' })}
            sx={{ display: 'inline-flex', float: 'left', width: '80px' }}>
            <ArrowBackIosNewIcon sx={{ mt: 3, ml: 1, fontSize: 65, color: 'lightGray' }}/>
          </IconButton> :
          <></>}
        <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'baseline', pr: 2 }}>
          <DialogTitle sx={{ ml: 3, fontSize: '30px' }}>Choose vendor</DialogTitle>
          {(locationState?.swapDosingId && dosing?.listingId) ?
            <IconButton
              disableRipple
              onClick={handleRemoveVendorFromProtocolClick}>
              <DeleteIcon sx={{ fontSize: '30px', color: 'white' }}/>
            </IconButton> :
            (dosing ?
              <></> :
              <Link
                variant='body2'
                underline='hover'
                style={{ cursor: 'pointer', marginRight: 20, fontSize: 16 }}
                onClick={handleSkipClick} >
              {"Skip"}
              </Link>)}
        </Stack>
        <Typography sx={{ paddingLeft: 6, fontSize: 14, variant: 'body2', color: 'gray' }}>
          {
            product?.brandName ? "for " : ""} {product?.brandName} {
            product?.productName}, {
            product?.amount ? String(Number(product?.amount)) : ""} {
            product?.amountUnit}
        </Typography>
        <DialogContent>
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            {listingsRetrieved ?
              <>
                <ListingsTable
                  protocolId={Number(params.protocolId)}
                  swapDosingId={locationState?.swapDosingId}
                  listings={individualListings}/>
                {(bundleListings.length && !locationState?.swapDosingId) ?
                  <>
                    <Box height={40}/>
                    <Typography variant='h5'>Included in bundle</Typography>
                    <ListingsTable
                      protocolId={Number(params.protocolId)}
                      swapDosingId={locationState?.swapDosingId}
                      listings={bundleListings}
                      bundles={true}
                      listingName={true}/>
                  </> :
                  <></>}
              </> :
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img src={loadingGif} width='100px'/>
              </Box>}
          </Stack>
        </DialogContent>
      </Dialog>
    </>);
};

export default ChooseListing;
